import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth, MODULES } from '@/contexts/AuthContext';
import {
  fetchNotificationsSummary,
  markNotificationsRead,
  closeNotification,
} from '@/pages/monitoring/services/monitoring.services';

const NotificationsContext = createContext(null);

const EMPTY_SUMMARY = {
  noLeidas: 0,
  pendientes: 0,
  porSeveridad: {},
  porTipo: {},
  recientes: [],
};

/**
 * Estado global de la campana de notificaciones de monitoreo.
 *
 * Hace polling del resumen igual que UpdateNotifier: intervalo fijo más un
 * refresco al volver a la pestaña. No consulta si no hay sesión o si el usuario
 * no tiene permiso sobre los módulos de monitoreo, para no generar tráfico ni
 * errores en roles que no verán la campana.
 */
export const NotificationsProvider = ({ children, pollIntervalMs = 60000 }) => {
  const { user, hasPermission } = useAuth();
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canView = !!user && hasPermission([MODULES.WORKPLACES, MODULES.REPORTS]);
  // Evita condiciones de carrera entre el intervalo y los refrescos manuales.
  const inFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!canView || inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    try {
      const resp = await fetchNotificationsSummary();
      setSummary({ ...EMPTY_SUMMARY, ...(resp?.data || {}) });
      setError(null);
    } catch (e) {
      // Silencioso: la campana no debe interrumpir el trabajo del usuario.
      setError(e.message || 'No se pudo cargar el resumen de notificaciones');
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    if (!canView) {
      setSummary(EMPTY_SUMMARY);
      return undefined;
    }

    refresh();
    const id = setInterval(refresh, pollIntervalMs);
    const onVisibility = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [canView, pollIntervalMs, refresh]);

  const markRead = useCallback(
    async ({ ids, all } = {}) => {
      const resp = await markNotificationsRead({ ids, all });
      await refresh();
      return resp;
    },
    [refresh],
  );

  const resolve = useCallback(
    async (id, payload) => {
      const resp = await closeNotification(id, {
        ...payload,
        resueltaPor: user ? { id: user.id, nombre: user.name } : undefined,
      });
      await refresh();
      return resp;
    },
    [refresh, user],
  );

  return (
    <NotificationsContext.Provider
      value={{ ...summary, loading, error, canView, refresh, markRead, resolve }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

/**
 * Devuelve el estado de la campana. Tolera la ausencia de provider para que
 * componentes aislados (o tests) no rompan.
 */
export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    return {
      ...EMPTY_SUMMARY,
      loading: false,
      error: null,
      canView: false,
      refresh: async () => {},
      markRead: async () => {},
      resolve: async () => {},
    };
  }
  return ctx;
};
